import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FaThLarge, FaList, FaArrowUp } from "react-icons/fa";
import { FaArrowRight } from "react-icons/fa6";
import { MiniStoreThemeContext } from "../../MiniStoreContexts/MiniStoreThemeContext";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ProductCard from "./Card/ProductCard";
import { CartContext } from "../../contexts/MiniStore/CartContext";
import { fetchCategories } from "../../DataApi/miniStore/fetchCategories";
import { fetchProductVariants } from "../../DataApi/miniStore/fetchProductVariants";
import { enqueueSnackbar } from "notistack";
import ProductCardModal from "./Card/ProductCardModal";
import Modal from "../../Components/Kanban/Modal/Modal";
import "react-responsive-pagination/themes/classic.css";
import { PropagateLoader } from "react-spinners";
import ScrollToTop from "react-scroll-to-top";
import { MINI_STORE_PAGINATION_LIMIT } from "../../utilities/constants";
import { TbFilterSearch } from "react-icons/tb";
import { useWindowWidth } from "../../CustomHooks/useWindowWidth";

const MsShop = () => {
  const { theme } = useContext(MiniStoreThemeContext);
  const {
    cart,
    addToCart,
    categories,
    setCategories,
    updateQuantity,
    products,
    setProducts,
    setVariants,
    setSelectedVariant,
    searchBy,
    setSearchBy,
  } = useContext(CartContext);
  const [isLoading, setIsLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [endOfList, setEndOfList] = useState("");
  const endingDivRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const [isGrid, setIsGrid] = useState(true);
  const [isProductWizardOpen, setIsProductWizardOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const windowWidth = useWindowWidth();
  const isNarrowScreen = windowWidth <= 846;

  const handleOpenProductModal = async (product, shouldNavigate = true) => {
    if (shouldNavigate) {
      navigate(`/storefront/shop?product_id=${product.id}`);
    }

    const getProductVariants = await fetchProductVariants({
      product_tmpl_id: product.id,
    });

    const formattedProductVariants = getProductVariants?.result.map(
      (product) => ({
        id: product?.id,
        title: product?.name || "",
        rating: product?.rating_avg || 0,
        quantity: getCartQuantity(product.id),
        currency: product?.currency_id,
        price: product?.lst_price.toFixed(2) || 0,
        newPrice: product?.pricelist_price.toFixed(2) || 0,
        has_discounted_price: product?.has_discounted_price || false,
        discount: 0,
        description: product?.description_sale || "",
        image_1920: product?.img_url,
        hasVariant: product?.attribute_line_ids?.length > 0 ? true : false,
        availableQty: product?.qty_available || 0,
        variant_ids: product?.product_variant_ids || [],
        attr_val_data: product?.attr_val_data || [],
      })
    );

    setVariants(formattedProductVariants);
    setSelectedVariant(formattedProductVariants[0]);
    setSelectedProduct(product);
    setIsProductWizardOpen(true);
  };
  
  const handleProductWizardClose = () => {
    setIsProductWizardOpen(false);
    navigate("/storefront/shop", { replace: true }); // remove query param
  };
  const slugify = (title) => {
    return title
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w\-]+/g, "");
  };
  const handleCategoryClick = (category) => {
    const categorySlug = slugify(category.title);
    navigate(`/storefront/shop/${categorySlug}/${category.id}`); // Navigate to the specific category's route
  };
  const getCartQuantity = (id) => {
    const cartItem = cart.find((item) => item.id === id);
    return cartItem?.quantity || 0;
  };
  const getCategoriesList = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const categoriesList = await fetchCategories({
        limit: MINI_STORE_PAGINATION_LIMIT,
        offset,
        query: "",
      });
      if (
        categoriesList?.status === 404 &&
        categoriesList?.message === "No categories found"
      ) {
        let value =
          categories?.length > 0
            ? "You've reached the end"
            : categoriesList?.message;
        setEndOfList(value);
      }
      if (categoriesList.status === 200) {
        // console.log(categoriesList);
        const formattedCategories = categoriesList?.result.map((category) => ({
          id: category?.id,
          title: category?.name,
          products: category.products.map((product) => ({
            id: product?.id,
            title: product?.name || "",
            rating: product?.rating_avg || 0,
            quantity: 0,
            currency: product?.currency_id?.[1],
            price: product?.lst_price.toFixed(2) || 0,
            newPrice: product?.pricelist_price.toFixed(2) || 0,
            has_discounted_price: product?.has_discounted_price || false,
            discount: 0,
            description: product?.description_sale || "",
            image_1920: product?.img_url,
            hasVariant: product?.attribute_line_ids?.length > 0 ? true : false,
            availableQty: product?.qty_available || 0,
            variant_ids: product?.product_variant_ids || [],
          })),
        }));
        setCategories((prev) => [...prev, ...formattedCategories]);
        setOffset(offset + MINI_STORE_PAGINATION_LIMIT);

        if (formattedCategories.length < categoriesList?.count) {
          setHasMore(true);
        } else {
          setHasMore(true);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
    setIsLoading(false);
  };
  const handleAddToCart = async (product) => {
    // console.log("product in handleAddToCart", product);
    if (product?.availableQty < 1) {
      enqueueSnackbar("Product quantity not available", { variant: "warning" });
      return false;
    }
    // console.log(
    //   "onAddToCart id and hasVariant",
    //   product.id,
    //   product?.hasVariant
    // );
    if (product?.hasVariant) {
      return handleOpenProductModal(product);
    } else {
      // console.log(product.variant_ids?.[0]);
      return addToCart({
        productId: product.variant_ids?.[0],
      });
    }
  };
  const fetchMoreData = () => {
    if (!isLoading && hasMore) {
      getCategoriesList();
    }
  };
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const productId = searchParams.get("product_id");

    if (!productId) return;

    const productObject = {
      id: parseInt(productId),
    };

    handleOpenProductModal(productObject, false); // false → don't navigate again
  }, [location.search]);
  
  
  
  

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchMoreData();
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );

    const current = endingDivRef.current;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [hasMore, isLoading, offset]);
  useEffect(() => {
    return () => {
      const body = document.getElementsByTagName("body")?.[0];
      if (body) {
        body.scrollIntoView();
      }

      setOffset(0);
      setCategories([]);
    };
  }, []);

  return (
    <>
      {location.pathname === "/storefront/shop" && (
        <Container>
          <FilterWrapper>
            <FilterHeading theme={theme}>
              Search by:{" "}
              {/* <TbFilterSearch
                size={20}
                style={{ marginRight: 6, color: theme.primaryColor.primary }}
              /> */}
            </FilterHeading>
            <CheckboxLabel theme={theme}>
              <input
                type="checkbox"
                checked={searchBy === "category"}
                onChange={() => setSearchBy("category")}
              />
              <span>Category</span>
            </CheckboxLabel>
            <CheckboxLabel theme={theme}>
              <input
                type="checkbox"
                checked={searchBy === "product"}
                onChange={() => {
                  setSearchBy("product");
                  setProducts([]);
                }}
              />
              <span>Product</span>
            </CheckboxLabel>
          </FilterWrapper>
          {isProductWizardOpen && selectedProduct && (
            <Modal
              isOpen={isProductWizardOpen}
              onClose={handleProductWizardClose}
              closeButton={true}
              miniStore={true}
            >
              {(() => {
                const cartItem = cart.find(
                  (item) => item.id === selectedProduct.variant_ids?.[0]
                );
                return (
                  <ProductCardModal
                    key={selectedProduct.id}
                    id={selectedProduct.id}
                    title={selectedProduct.title}
                    rating={selectedProduct.rating}
                    quantity={cartItem?.quantity || 0}
                    currency={selectedProduct.currency}
                    description={selectedProduct.description}
                    price={selectedProduct.price}
                    newPrice={selectedProduct.newPrice}
                    has_discounted_price={selectedProduct.has_discounted_price}
                    img={selectedProduct.image_1920}
                    onQuantityChange={updateQuantity}
                    onAddToCart={addToCart}
                  />
                );
              })()}
            </Modal>
          )}
          {searchBy === "product" ? (
            <>
              <ToggleButtons>
                <IconButton
                  theme={theme}
                  isActive={isGrid}
                  onClick={() => setIsGrid(true)}
                >
                  <FaThLarge />
                </IconButton>
                <IconButton
                  theme={theme}
                  isActive={!isGrid}
                  onClick={() => setIsGrid(false)}
                >
                  <FaList />
                </IconButton>
              </ToggleButtons>
              <CardsWrapper isGrid={isGrid}>
                {Array.isArray(products) && products.length > 0 ? (
                  products.map((product) => {
                    const matchingCartItems = cart.filter((item) =>
                      product.variant_ids?.includes(item.id)
                    );
                    const quantity = matchingCartItems.reduce(
                      (sum, item) => sum + (item.quantity || 0),
                      0
                    );
                    return (
                      <ProductCard
                        key={product.id}
                        id={product.id}
                        variant_ids={product?.variant_ids}
                        title={product.title}
                        rating={product.rating}
                        quantity={quantity || 0}
                        currency={product.currency}
                        price={product.price}
                        newPrice={product.newPrice}
                        img={product.image_1920}
                        onClick={() => handleOpenProductModal(product)}
                        onQuantityChange={(id, change) =>
                          updateQuantity({
                            productId: product.variant_ids?.[0],
                            qtyChange: change,
                          })
                        }
                        onAddToCart={() => handleAddToCart(product)}
                        viewType={isGrid ? "grid" : "list"}
                        hasVariant={product.hasVariant}
                      />
                    );
                  })
                ) : isLoading ? (
                  <PropagateLoader color={theme.primaryColor.primary} />
                ) : (
                  <p className="text-center w-100">No Data</p>
                )}
              </CardsWrapper>
              <ScrollToTop
                smooth
                color={theme.textColor.primary}
                style={{ backgroundColor: theme.primaryColor.primary }}
              />
            </>
          ) : (
            <>
              {Array.isArray(categories) &&
                categories.length > 0 &&
                categories.map((category) => (
                  <CategoryWrapper key={category.id} theme={theme}>
                    <CardsWrapper>
                      {category.products
                        .slice(0, isNarrowScreen ? 2 : 3)
                        .map((product) => {
                          const matchingCartItems = cart.filter((item) =>
                            product.variant_ids?.includes(item.id)
                          );
                          const quantity = matchingCartItems.reduce(
                            (sum, item) => sum + (item.quantity || 0),
                            0
                          );
                          return (
                            <ProductCard
                              key={product.id}
                              id={product.id}
                              variant_ids={product?.variant_ids}
                              title={product.title}
                              rating={product.rating}
                              quantity={quantity || 0}
                              currency={product.currency}
                              description={product.description}
                              price={product.price}
                              newPrice={product.newPrice}
                              has_discounted_price={
                                product.has_discounted_price
                              }
                              img={product.image_1920}
                              onClick={() => handleOpenProductModal(product)}
                              onQuantityChange={(id, change) =>
                                updateQuantity({
                                  productId: product.variant_ids?.[0],
                                  qtyChange: change,
                                })
                              }
                              onAddToCart={() => handleAddToCart(product)}
                              viewType={isGrid ? "grid" : "list"}
                              hasVariant={product.hasVariant}
                            />
                          );
                        })}
                    </CardsWrapper>
                    <CategoryTitleWrapper
                      onClick={() => handleCategoryClick(category)}
                      theme={theme}
                    >
                      <CategoryTitle theme={theme}>
                        {category.title}
                      </CategoryTitle>
                      <ShowMoreWrapper>
                        <ShowMoreText theme={theme}>View</ShowMoreText>
                        <StyledArrow theme={theme} />
                      </ShowMoreWrapper>
                    </CategoryTitleWrapper>
                    <StyledLine theme={theme} />
                  </CategoryWrapper>
                ))}
              <ScrollToTop
                smooth
                color={theme.textColor.primary}
                component={
                  <FaArrowUp
                    style={{
                      width: "28px",
                      height: "28px",
                      color: theme.textColor.primary,
                    }}
                  />
                }
                style={{ backgroundColor: theme.primaryColor.primary }}
              />
              <div
                ref={endingDivRef}
                style={{
                  width: "100%",
                  textAlign: "center",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {isLoading ? (
                  <PropagateLoader color={theme.primaryColor.primary} />
                ) : (
                  endOfList
                )}
              </div>
            </>
          )}
        </Container>
      )}
      <Outlet />
    </>
  );
};

export default MsShop;

// Styled Components

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 10px 0;
  @media (max-width: 768px) {
    gap: 10px;
    flex-direction: column;
    align-items: start;
    justify-content: start;
  }
`;

const FilterHeading = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.primaryColor.primary};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 16px;
  color: ${({ theme }) => theme.primaryColor.secondary};

  input[type="checkbox"] {
    appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid ${({ theme }) => theme.primaryColor.secondary};
    border-radius: 0.15rem;
    position: relative;
    cursor: pointer;
  }

  input[type="checkbox"]:checked {
    background-color: ${({ theme }) => theme.primaryColor.primary};
    border-color: ${({ theme }) => theme.primaryColor.primary};
  }

  input[type="checkbox"]:checked::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 6px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;
const CategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 20px;
  background: #fff;
  padding: 16px;
  border-radius: 0.15rem;
  border-width: 1px;
  border: 1px solid #dee2e6;
  // box-shadow: ${({ theme }) => theme.boxShadow.primary};
  // transition: transform 0.2s ease-in-out;
  @media (max-width: 425px) {
    gap: 10px;
    padding: 10px;
  }
`;
const CategoryTitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  cursor: pointer;
  padding: 10px 0;
  width: 100%;
`;

const CategoryTitle = styled.div`
  font-family: Outfit, sans-serif;
  font-size: 28px;
  font-weight: 500;
  line-height: 29px;
  color: ${({ theme }) => theme.primaryColor.primary};
`;

const ShowMoreWrapper = styled.sub`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-left: 1rem;
`;

const ShowMoreText = styled.div`
  font-family: Outfit, sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.primaryColor.primary};
`;

const StyledArrow = styled(FaArrowRight)`
  color: ${({ theme }) => theme.primaryColor.primary};
  font-size: 16px;
`;

const StyledLine = styled.div`
  width: 223px;
  height: 5px;
  background-color: ${({ theme }) => theme.primaryColor.primary};
  box-shadow: ${({ theme }) => theme.boxShadow?.secondary};
  border-radius: 0.5rem;
`;
const ProductsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;
const ToggleButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const IconButton = styled.button`
  background: ${({ isActive, theme }) =>
    isActive ? theme.primaryColor.primary : "#ccc"};
  color: ${({ theme }) => theme.textColor.primary};
  border: none;
  padding: 10px 15px;
  border-radius: 0.15rem;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    opacity: 0.85;
  }
`;

const CardsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 900px;
  place-items: start center;
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 425px) {
    gap: 10px;
  }
`;
